import React, { useEffect, useRef, useState } from "react";
import { CareerTimelineType } from "../../Models";

import useApiToken from "../../hooks/useApiToken";
import TimelineModal from "../Shared/TimelineModal";
import CareerTimeline from "../Candidates/CareerTimeline";
import StraightTimeline from "../Candidates/StraightTimerline";
import ReassignSlotModal from "./ReassignSlotModal";
import Notification from "../Shared/Notification";
import { useSearchParams } from "react-router-dom";
import { title } from "process";
import { getAssessmentSlotStatus, GetCandidateTimeline, resetCandidateAssessmentScore, updateSingleCandidateAssessmentSlotStatus } from "../../api/candidates.api";
import { resetCandidateWindow, sendAssessmentLinkWithoutSeb, approveRefreshLimitResume } from "../../api/assessmentslots.api";
import { WindowResetAlert } from "../../types/windowReset.types";
import { RefreshLimitPauseAlert } from "../../types/refreshLimitPause.types";
import CandidateAlertBadge from "./CandidateAlertBadge";
import { isAssessmentWindowExpired, parseSlotTime } from "./CandidateFunnelBar";

interface SlotCandidate {
  candidateId: string;
  candidateName: string;
  candidateEmail: string;
  hasBooked: boolean;
  status: string;
  score: number | null;
  startTime: string | null;
  endTime: string | null;
  adminStatus: number; // -1=cancelled, 0=paused, 1=started
  hasPendingWindowReset?: boolean;
  // Separate from adminStatus: this is the automatic refresh-limit hold, not an admin pause.
  isPausedForRefreshLimit?: boolean;
  usedSeb: boolean;
  bypassedSeb: boolean;
  takenWithoutSeb: boolean;
}

/**
 * A row can be bulk-selected when the candidate is either waiting to start ("Booked")
 * or mid-test ("Ongoing"). Ongoing candidates matter most in the exam hall — they are
 * the ones whose SEB crashed and who need a window reset or a plain link.
 * Exported so the header select-all in SlotCandidates stays in sync with the row checkbox.
 */
export const isCandidateSelectable = (candidate: { status: string }) =>
  candidate.status === "Booked" || candidate.status === "Ongoing";

interface SlotCandidateRowProps {
  candidate: SlotCandidate;
  roleId?: string;
  roleStatusId?: string;
  assessmentDuration: number; // in minutes
  assessmentName?: string;
  isSelected?: boolean;
  handleSelectCandidate: (id: string) => void;
  onStatusChange?: () => void;
  slotId : string | undefined;
  windowResetAlert?: WindowResetAlert;
  /** Called after a successful Reset Window — broadcasts to all PCs + clears localStorage */
  onWindowResetGranted?: (candidateId: string, slotId: string) => Promise<void>;
  /** Called when recruiter dismisses without acting — broadcasts to all PCs + clears localStorage */
  onWindowResetDismissed?: (candidateId: string, slotId: string) => Promise<void>;
  refreshLimitPauseAlert?: RefreshLimitPauseAlert;
  /** Broadcasts that the pause was actioned, so every recruiter PC drops the badge together. */
  onRefreshLimitPauseResolved?: (candidateId: string, slotId: string) => Promise<void>;
}

const SlotCandidateRow: React.FC<SlotCandidateRowProps> = ({
  candidate,
  roleId,
  roleStatusId,
  assessmentDuration,
  assessmentName,
  isSelected = false,
  handleSelectCandidate,
  onStatusChange,
  slotId,
  windowResetAlert,
  onWindowResetGranted,
  onWindowResetDismissed,
  refreshLimitPauseAlert,
  onRefreshLimitPauseResolved,
}) => {
  const token = useApiToken();
  const timeoutIdRef = useRef<number | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(false);
  const [timelineData, setTimelineData] = useState<CareerTimelineType | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>("--");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
  const [adminOptionFlag , setAdminOptionFlag] = useState<number>(0)
  const [isResettingWindow, setIsResettingWindow] = useState(false);
  const [isSendingWithoutSeb, setIsSendingWithoutSeb] = useState(false);
  // Popover open-state and ref now live inside CandidateAlertBadge, which owns both alert
  // popovers. Kept commented rather than deleted per review preference.
  const [isAlertPopoverOpen, setIsAlertPopoverOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // const alertPopoverRef = useRef<HTMLDivElement>(null);
  const[showNotification , setShowNotification] = useState<boolean>(false)
  const [notificationContent, setnotificationContent] = useState<{ title: string; description: string; type: "success" | "error" | "info"; }>({
    title: "",
    description: "",
    type: "success" 
  });

  useEffect(() => {
    if (candidate.adminStatus === -1) {
      setTimeRemaining("Cancelled");
      return;
    }
    if (candidate.adminStatus === 0) {
      setTimeRemaining("Paused");
      return;
    }
    if (candidate.endTime || !candidate.startTime) {
      setTimeRemaining("--");
      return;
    }

    const calculateTimeRemaining = () => {
      const startTimeMs = parseSlotTime(candidate.startTime!).getTime();
      const durationMs = assessmentDuration * 60 * 1000; // convert minutes to ms
      const expectedEndTime = startTimeMs + durationMs;
      const remaining = expectedEndTime - Date.now();

      if (remaining <= 0) {
        setTimeRemaining("00:00");
      } else {
        const mins = Math.floor(remaining / 60000);
        const secs = Math.floor((remaining % 60000) / 1000);
        setTimeRemaining(`${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`);
      }
    };

    // Calculate immediately
    calculateTimeRemaining();

    // Update every second
    const interval = setInterval(calculateTimeRemaining, 1000);

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, [candidate.startTime, candidate.endTime, assessmentDuration, candidate.adminStatus]);

  // Handle click outside dropdown menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  // Click-outside for the alert popover now lives in CandidateAlertBadge, so both alert kinds
  // get it from one place. Commented rather than deleted.
  // useEffect(() => {
  //   const handleClickOutside = (event: MouseEvent) => {
  //     if (alertPopoverRef.current && !alertPopoverRef.current.contains(event.target as Node)) {
  //       setIsAlertPopoverOpen(false);
  //     }
  //   };
  //   if (isAlertPopoverOpen) {
  //     document.addEventListener("mousedown", handleClickOutside);
  //   }
  //   return () => {
  //     document.removeEventListener("mousedown", handleClickOutside);
  //   };
  // }, [isAlertPopoverOpen]);
  useEffect(() => {
    const fetchSlotStatus = async () => {
      if (slotId && candidate.hasBooked) {
        try {
          const data = await getAssessmentSlotStatus(candidate.candidateId, slotId, token!);
          if (typeof data.statusCode === "number") setAdminOptionFlag(data.statusCode);
        } catch (err) {
          console.error("Failed to fetch assessment slot status:", err);
        }
      }
    };
    fetchSlotStatus();
  }, [slotId, candidate.hasBooked]);

  const handleReassignComplete = () => {
    setIsReassignModalOpen(false);
    onStatusChange?.();
  };

  const handleTimelineHover = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!roleId || !token) return;

    setTimelineLoading(true);
    try {
      const candidateTimeline = await GetCandidateTimeline(
        roleId,
        candidate.candidateId,
        token
      );
      setTimelineData(candidateTimeline);
      setTimelineError(false);
      setTimelineLoading(false);
    } catch (error) {
      console.error("Error fetching candidate timeline:", error);
      setTimelineError(true);
      setTimelineLoading(false);
    }
    setIsModalOpen(true);
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    timeoutIdRef.current = window.setTimeout(() => {
      handleTimelineHover(e);
    }, 500);
  };

  const handleMouseLeave = () => {
    if (timeoutIdRef.current) {
      window.clearTimeout(timeoutIdRef.current);
    }
    setTimelineData(null);
    setIsModalOpen(false);
  };

  const getStatusBadge = () => {
    if (candidate.adminStatus === -1) {
      return (
        <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
          Cancelled
        </span>
      );
    }
    if (candidate.adminStatus === 0) {
      return (
        <span className="inline-flex items-center rounded-full bg-orange-50 px-2 py-1 text-xs font-medium text-orange-700 ring-1 ring-inset ring-orange-600/20">
          Paused
        </span>
      );
    }
    // Checked after adminStatus so an explicit admin pause/cancel still wins the status column.
    if (candidate.isPausedForRefreshLimit) {
      return (
        <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
          Paused — Review
        </span>
      );
    }
    switch (candidate.status) {
      case "Completed":
        return (
          <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
            Completed
          </span>
        );
      case "Ongoing":
        // The API keeps reporting "Ongoing" for anyone who started and never submitted.
        // Once the window has run out they are not writing anything any more — the row
        // already shows 00:00 remaining, so the badge must agree.
        if (isAssessmentWindowExpired(candidate, assessmentDuration)) {
          return (
            <span
              title="Assessment window elapsed without a submission"
              className="inline-flex items-center rounded-full bg-rose-50 px-2 py-1 text-xs font-medium text-rose-700 ring-1 ring-inset ring-rose-600/20"
            >
              Expired
            </span>
          );
        }
        return (
          <span className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20">
            Ongoing
          </span>
        );
      case "Booked":
        return (
          <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
            Booked
          </span>
        );
      case "Not Booked":
      default:
        return (
          <span className="inline-flex items-center rounded-full bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-600/20">
            Not Booked
          </span>
        );
    }
  };

  const isReassignEnabled = candidate.status === "Booked" || candidate.status === "Completed" || candidate.status === "Ongoing";
  const isAdminActionsEnabled = candidate.status === "Booked" || candidate.status === "Ongoing";
  const isSelectable = isCandidateSelectable(candidate);
  
  function GivePopupNotification({
    show,
    setShow,
    title,
    description,
    type 
  }: {
    show: boolean,
    setShow: React.Dispatch<React.SetStateAction<boolean>>,
    title: string,
    description: string,
    type:  "success" | "error" | "info"
  }) {
    
    // only set notification content here, then setShowNotification(true), then reset notification content to empty
    setnotificationContent({
      title,
      description,
      type,
    });
    setShow(true);
    
  }
  // Function to handle admin option click for a candidate row
  const handleAdminOptionClick = async (option: number) => {
    if (!slotId || !candidate.candidateId) return;
    if (option === 1 || option === -1 || option === 0) {
      try {
        console.log("handling candidate id , "  , candidate.candidateId , slotId , option)
        const resp = await updateSingleAssessmentSlotStatus( candidate.candidateId ,slotId , option)
        GivePopupNotification({
          show: true,
          setShow: setShowNotification,
          title: `Test ${resp.status}`,
          description: `${candidate.candidateName} Test is ${resp.status}`,
          type: "success",
        });
        console.log(resp)
        onStatusChange?.();
      } catch (err) {
        console.error("Failed to update candidate slot status:", err);
      }
    }
  };
  const updateSingleAssessmentSlotStatus = async (
    candidateId: string,
    slotId: string,
    status: number 
  ): Promise<any> => {
    try {
      const result = await updateSingleCandidateAssessmentSlotStatus(candidateId ,slotId, status, token!);
      return result;
    } catch (err) {
      console.error('Failed to update single assessment slot status:', err);
      throw err;
    }
  };
  // Function to reset the candidate's assessment score for this slot/role status
  const handleResetAssessmentScore = async () => {
    if (!candidate.candidateId || !roleStatusId) return;
    try {
      const result = await resetCandidateAssessmentScore(candidate.candidateId, roleStatusId, token!);
      GivePopupNotification({
        show: true,
        setShow: setShowNotification,
        title: "Score Reset",
        description: `${candidate.candidateName}'s test score has been reset.`,
        type: "success",
      });
      console.log("Assessment score reset result:", result);
      if (onStatusChange) onStatusChange();
    } catch (err) {
      console.error("Failed to reset assessment score:", err);
    }
  };

  const handleResetWindowFromAlert = async () => {
    if (!slotId || !candidate.candidateId || !token || isResettingWindow) return;
    setIsResettingWindow(true);
    try {
      await resetCandidateWindow(candidate.candidateId, slotId, token);
      GivePopupNotification({
        show: true,
        setShow: setShowNotification,
        title: "Window Reset",
        description: `${candidate.candidateName}'s assessment window has been reset.`,
        type: "success",
      });
      // Broadcast to all PCs; they handle state + localStorage cleanup via SignalR event
      await onWindowResetGranted?.(candidate.candidateId, slotId);
      onStatusChange?.();
    } catch (err) {
      console.error("Failed to reset assessment window:", err);
      GivePopupNotification({
        show: true,
        setShow: setShowNotification,
        title: "Reset Failed",
        description: "Could not reset the assessment window. Please try again.",
        type: "error",
      });
    } finally {
      setIsResettingWindow(false);
      setIsAlertPopoverOpen(false);
    }
  };

  const handleSendWithoutSeb = async () => {
    if (!slotId || !candidate.candidateId || !token || isSendingWithoutSeb) return;
    setIsSendingWithoutSeb(true);
    try {
      await sendAssessmentLinkWithoutSeb(candidate.candidateId, slotId, token);
      GivePopupNotification({
        show: true,
        setShow: setShowNotification,
        title: "Link Sent",
        description: `Non-SEB assessment link sent to ${candidate.candidateName}.`,
        type: "success",
      });
      onStatusChange?.();
    } catch (err) {
      console.error("Failed to send assessment link without SEB:", err);
      GivePopupNotification({
        show: true,
        setShow: setShowNotification,
        title: "Send Failed",
        description: "Could not send the assessment link. Please try again.",
        type: "error",
      });
    } finally {
      setIsSendingWithoutSeb(false);
    }
  };

  const handleDismissAlert = async () => {
    if (!slotId || !candidate.candidateId) return;
    setIsAlertPopoverOpen(false);
    // Broadcast to all PCs; they handle state + localStorage cleanup via SignalR event
    await onWindowResetDismissed?.(candidate.candidateId, slotId);
  };

  // Resumes the candidate with the time they had left. The server also resets the deliberate
  // reload count to 1 in the same transaction - without that they'd re-pause on the next reload.
  const handleAllowContinue = async () => {
    if (!slotId || !candidate.candidateId || !token) return;
    try {
      await approveRefreshLimitResume(candidate.candidateId, slotId, token);
      GivePopupNotification({
        show: true,
        setShow: setShowNotification,
        title: "Exam Resumed",
        description: `${candidate.candidateName} may continue their exam.`,
        type: "success",
      });
      await onRefreshLimitPauseResolved?.(candidate.candidateId, slotId);
      onStatusChange?.();
    } catch (err) {
      console.error("Failed to approve exam resume:", err);
      GivePopupNotification({
        show: true,
        setShow: setShowNotification,
        title: "Resume Failed",
        description: "Could not resume the exam. Please try again.",
        type: "error",
      });
    }
  };

  // Clears the badge only. The candidate stays paused, and the DB flag brings the badge back on
  // the next refetch so a dismissed pause can't be forgotten about entirely.
  const handleDismissPauseAlert = async () => {
    if (!slotId || !candidate.candidateId) return;
    await onRefreshLimitPauseResolved?.(candidate.candidateId, slotId);
  };

  const formatAlertTime = (iso: string) => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleTimeString();
  };


  

  return (
    
    <div className="border-b border-gray-200 dark:border-gray-700">
      <div className="grid grid-cols-[auto_2fr_1fr_1fr_1fr_1fr_auto] gap-4 items-center hover:bg-gradient-to-r from-white via-gray-100 to-white px-4 sm:px-6 lg:px-8 py-4 dark:from-darkbg dark:via-darkbglight dark:to-darkbg">
        <input
          type="checkbox"
          checked={isSelected}
          disabled={!isSelectable}
          className={`form-checkbox h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 ${
            !isSelectable ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            if (!isSelectable) return;
            handleSelectCandidate(candidate.candidateId);
          }}
          onChange={() => {}}
        />
        <div className="relative">
          <div className="flex items-center gap-2">
            <h1
              className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-300 inline-block"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {candidate.candidateName}
              {timelineData?.candidateId === candidate.candidateId && (
                <TimelineModal
                  open={isModalOpen}
                  setOpen={setIsModalOpen}
                  content={timelineData}
                  loading={timelineLoading}
                  timelineError={timelineError}
                  timeline={
                    timelineData?.timeline.length <= 3 ? (
                      <StraightTimeline timeline={timelineData?.timeline} />
                    ) : (
                      <CareerTimeline
                        timeline={timelineData?.timeline || []}
                      />
                    )
                  }
                />
              )}
            </h1>

            {/* Window-reset badge. Dual condition: the DB flag survives a portal refresh, the
                live alert carries the reason/timestamp that only the broadcast knows. */}
            {(candidate.hasPendingWindowReset || windowResetAlert) && (
              <CandidateAlertBadge
                label="Reset Requested"
                tone="amber"
                title={candidate.candidateName}
                detailLines={[
                  windowResetAlert?.reason && `Reason: ${windowResetAlert.reason.trim()}`,
                  windowResetAlert?.requestedAt &&
                    `Requested at ${formatAlertTime(windowResetAlert.requestedAt)}`,
                ]}
                actions={[
                  {
                    label: "Reset Window",
                    busyLabel: "Resetting...",
                    variant: "primary",
                    onClick: handleResetWindowFromAlert,
                  },
                  { label: "Dismiss", variant: "neutral", onClick: handleDismissAlert },
                ]}
              />
            )}

            {/* Refresh-limit pause badge. Red rather than amber because the exam is halted right
                now - the candidate is sitting on a blank pause screen until someone acts. */}
            {(candidate.isPausedForRefreshLimit || refreshLimitPauseAlert) && (
              <CandidateAlertBadge
                label="Paused — Refresh Limit"
                tone="red"
                title={candidate.candidateName}
                detailLines={[
                  refreshLimitPauseAlert?.refreshCount != null &&
                    `Deliberate reloads: ${refreshLimitPauseAlert.refreshCount}`,
                  refreshLimitPauseAlert?.pausedAt &&
                    `Paused at ${formatAlertTime(refreshLimitPauseAlert.pausedAt)}`,
                ]}
                actions={[
                  {
                    label: "Allow Continue",
                    busyLabel: "Resuming...",
                    variant: "primary",
                    onClick: handleAllowContinue,
                  },
                  { label: "Dismiss", variant: "neutral", onClick: handleDismissPauseAlert },
                ]}
              />
            )}
          </div>
          <div className="mt-1 text-sm text-gray-500 truncate">
            {candidate.candidateEmail}
          </div>
        </div>

        <div className="text-center text-sm text-gray-700 dark:text-gray-300">
          {candidate.score !== null ? candidate.score : "--"}
        </div>

        <div className="text-center text-sm text-gray-700 dark:text-gray-300">
          {candidate.startTime
            ? parseSlotTime(candidate.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : "--"}
        </div>

        <div className="text-center text-sm text-gray-700 dark:text-gray-300">
          {timeRemaining}
        </div>

        <div className="flex items-center justify-center">
          {candidate.takenWithoutSeb ? (
            <span
              title="Exam taken via normal browser (Recruiter Authorized)"
              className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-700 px-2.5 py-0.5 text-xs font-semibold text-gray-600 dark:text-gray-300 ring-1 ring-gray-300 dark:ring-gray-600"
            >
              {/* Globe icon */}
              <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              Normal (Allowed)
            </span>
          ) : candidate.usedSeb && candidate.bypassedSeb ? (
            <span
              title="Candidate started the exam in Safe Exam Browser, but switched to a normal browser mid-exam."
              className="inline-flex items-center gap-1 rounded-full bg-red-100 dark:bg-red-900/40 px-2.5 py-0.5 text-xs font-semibold text-red-800 dark:text-red-300 ring-1 ring-red-300 dark:ring-red-700"
            >
              {/* Warning icon */}
              <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              Bypassed SEB
            </span>
          ) : candidate.usedSeb && !candidate.bypassedSeb ? (
            <span
              title="Exam taken securely via Safe Exam Browser"
              className="inline-flex items-center gap-1 rounded-full bg-green-100 dark:bg-green-900/40 px-2.5 py-0.5 text-xs font-semibold text-green-800 dark:text-green-300 ring-1 ring-green-300 dark:ring-green-700"
            >
              {/* Shield icon */}
              <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              SEB
            </span>
          ) : candidate.startTime ? (
            <span
              title="Candidate ignored the SEB requirement completely"
              className="inline-flex items-center gap-1 rounded-full bg-yellow-100 dark:bg-yellow-900/40 px-2.5 py-0.5 text-xs font-semibold text-yellow-800 dark:text-yellow-300 ring-1 ring-yellow-300 dark:ring-yellow-700"
            >
              {/* Alert icon */}
              <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              Never Used SEB
            </span>
          ) : (
            <span className="text-gray-400 text-sm">--</span>
          )}
        </div>

        <div className="flex items-center justify-end gap-3">
          {getStatusBadge()}
          <div className="relative inline-block text-left" ref={menuRef}>
            <div>
              <button
                type="button"
                className="-my-2 flex items-center rounded-full bg-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setIsMenuOpen(!isMenuOpen);
                }}
              >
                <span className="sr-only">Open options</span>
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z"
                  />
                </svg>
              </button>
            </div>

            {isMenuOpen && (
              <div
                className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-darkbglight"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div className="py-1">
                  {/* Reassign button - enabled only for Booked status */}
                  <button
                    type="button"
                    disabled={!isReassignEnabled}
                    className={`block w-full px-4 py-2 text-left text-sm ${
                      isReassignEnabled
                        ? "text-black-600 dark:text-black-400 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                        : "text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isReassignEnabled) return;
                      setIsMenuOpen(false);
                      setIsReassignModalOpen(true);
                    }}
                  >
                    Reassign to Slot
                  </button>
                  <button
                    type="button"
                    disabled={!isAdminActionsEnabled}
                    className={`block w-full px-4 py-2 text-left text-sm ${
                      isAdminActionsEnabled
                        ? "text-gray-700 dark:text-gray-400 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 cursor-pointer"
                        : "text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isAdminActionsEnabled) return;
                      handleAdminOptionClick(1)
                    }}
                  >
                   Start Test
                  </button>
                  <button
                    type="button"
                    disabled={!isAdminActionsEnabled}
                    className={`block w-full px-4 py-2 text-left text-sm ${
                      isAdminActionsEnabled
                        ? "text-gray-700 dark:text-gray-400 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 cursor-pointer"
                        : "text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isAdminActionsEnabled) return;
                      handleAdminOptionClick(0)
                    }}
                  >
                    Pause Test
                  </button>
                  <button
                    type="button"
                    disabled={!isAdminActionsEnabled}
                    className={`block w-full px-4 py-2 text-left text-sm ${
                      isAdminActionsEnabled
                        ? "text-gray-700 dark:text-gray-400 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 cursor-pointer"
                        : "text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isAdminActionsEnabled) return;
                      handleAdminOptionClick(-1)
                    }}
                  >
                    Cancel Test
                  </button>
                  <button
                    type="button"
                    disabled={!isAdminActionsEnabled || isSendingWithoutSeb}
                    title="Email the plain assessment link (no Safe Exam Browser required)"
                    className={`block w-full px-4 py-2 text-left text-sm ${
                      isAdminActionsEnabled && !isSendingWithoutSeb
                        ? "text-gray-700 dark:text-gray-400 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 cursor-pointer"
                        : "text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isAdminActionsEnabled || isSendingWithoutSeb) return;
                      setIsMenuOpen(false);
                      handleSendWithoutSeb();
                    }}
                  >
                    {isSendingWithoutSeb ? "Sending..." : "Resend Link (No SEB)"}
                  </button>
                {candidate.status === "Completed" && (
                  <button
                    type="button"
                    className="block w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-red-800 dark:hover:text-red-200 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResetAssessmentScore();
                    }}
                  >
                    Reset Test
                  </button>
                )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reassign Slot Modal */}
      {isReassignModalOpen && roleStatusId && slotId && (
        <ReassignSlotModal
          isOpen={isReassignModalOpen}
          onClose={() => setIsReassignModalOpen(false)}
          candidateId={candidate.candidateId}
          candidateName={candidate.candidateName}
          currentSlotId={candidate.hasBooked ? slotId : null}
          roleStatusId={roleStatusId}
          hasCurrentBooking={candidate.hasBooked}
          onReassignComplete={handleReassignComplete}
        />
      )}
       <Notification
        show={showNotification}
        setShow={setShowNotification}
        title={notificationContent.title}
        description={notificationContent.description}
        type={notificationContent.type}
      />
    </div>
  );
};

export default SlotCandidateRow;
